import { Info } from "lucide-react";
import { Card, CardContent } from "../../misc/Card";
import {
    CampaignPlatform,
    PlatformFooterCardProps,
    SocialPlatforms,
} from "../types";
import { Textarea } from "../../misc/Textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../listings/slideshow/Tooltip";
import { Button } from "../../misc/Button";
import { Switch } from "../../misc/Switch";
import { useEffect, useState } from "react";
import PostFooterInfo from "../PostFooterInfo";
import { useIsMobile } from "../../../hooks/useIsMobile";
import ConfirmPopup from "../../misc/ConfirmPopup";
import {
    FOOTER_OFF_CANCEL_TEXT,
    FOOTER_OFF_CONFIRM_TEXT,
    FOOTER_OFF_WARNING_MESSAGE,
    FOOTER_OFF_WARNING_MESSAGE_TWO,
    FOOTER_OFF_WARNING_TITLE,
} from "../../../utils/sharedListingFooter";

// Utility: compute “weighted” length where each '\n' counts as 2 chars
const computeWeightedLength = (text: string): number => {
    const newlineMatches = text.match(/\n/g);
    const newlineCount = newlineMatches ? newlineMatches.length : 0;
    // raw length counts each '\n' as 1, so add 1 extra per newline
    return text.length + newlineCount;
};

// Utility: trim a string so its weighted length ≤ maxChars
const trimToLimit = (text: string, maxChars: number): string => {
    let weightedCount = 0;
    let idx = 0;

    while (idx < text.length) {
        const char = text[idx];
        if (char === "\n") {
            // newline counts as 2
            if (weightedCount + 2 > maxChars) break;
            weightedCount += 2;
        } else {
            // any other char counts as 1
            if (weightedCount + 1 > maxChars) break;
            weightedCount += 1;
        }
        idx++;
    }

    return text.slice(0, idx);
};

const PlatformFooterCard: React.FC<PlatformFooterCardProps> = ({
    platform,
    setPlatforms,
    sharedListing = false,
    footerOffAcknowledged = false,
    onFooterOffAcknowledged,
}) => {
    const isMobile = useIsMobile(900);
    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [confirmFooterOff, setConfirmFooterOff] = useState(false);

    // Determine max allowed characters based on platform
    const maxChars = platform.name === SocialPlatforms.Twitter ? 280 : 300;

    // Handler for toggling footer inclusion (boolean)
    const handleFooterToggle = (
        platformName: string,
        includeFooter: boolean
    ) => {
        setPlatforms((prevPlatforms: CampaignPlatform[]) => {
            return prevPlatforms.map((platformObj) => {
                if (platformObj.name === platformName) {
                    return { ...platformObj, includeFooter };
                }
                return platformObj;
            });
        });
    };

    // Handler to update footer type ("comment" or "in-post")
    const handleFooterTypeChange = (
        platformName: string,
        newType: "comment" | "in-post"
    ) => {
        setPlatforms((prevPlatforms) => {
            return prevPlatforms.map((platformObj) => {
                if (platformObj.name === platformName) {
                    // For Twitter, if threads are disabled, force footerType to "comment"
                    if (
                        platformObj.name.toLowerCase() === "twitter" &&
                        !platformObj.threads
                    ) {
                        return { ...platformObj, footerType: "comment" };
                    }
                    return { ...platformObj, footerType: newType };
                }
                return platformObj;
            });
        });
    };

    // Switching the footer off on a colleague's listing removes the listing
    // agent's attribution, so the user confirms once per wizard visit first.
    const handleFooterSwitch = (checked: boolean) => {
        if (!checked && sharedListing && !footerOffAcknowledged) {
            setConfirmFooterOff(true);
            return;
        }
        handleFooterToggle(platform.name, checked);
    };

    const confirmFooterOffAndTurnOff = () => {
        setConfirmFooterOff(false);
        onFooterOffAcknowledged?.();
        handleFooterToggle(platform.name, false);
    };

    const handleFooterChange = (value: string) => {
        // First, trim to the limit
        const trimmed = trimToLimit(value, maxChars);

        setPlatforms((prevPlatforms: CampaignPlatform[]) => {
            return prevPlatforms.map((platformObj) => {
                if (platformObj.name === platform.name) {
                    return { ...platformObj, footer: trimmed };
                }
                return platformObj;
            });
        });
    };

    // Compute the current weighted count for UI display
    const currentWeightedCount = computeWeightedLength(platform.footer);

    useEffect(() => {
        // Get radio button elements by their unique IDs
        const radioComment = document.getElementById(
            `${platform.name}-comment-radio`
        );
        const radioInPost = document.getElementById(
            `${platform.name}-inpost-radio`
        );

        // Define event handler functions for comment radio
        const onCommentMouseOver = () => {
            if (radioComment) radioComment.style.accentColor = "#193456";
        };
        const onCommentMouseOut = () => {
            if (radioComment) radioComment.style.accentColor = "#193456";
        };

        // Define event handler functions for in-post radio
        const onInPostMouseOver = () => {
            if (radioInPost) radioInPost.style.accentColor = "#193456";
        };
        const onInPostMouseOut = () => {
            if (radioInPost) radioInPost.style.accentColor = "#193456";
        };

        // Set default accent color and add event listeners
        if (radioComment) {
            radioComment.style.accentColor = "#193456";
            radioComment.addEventListener("mouseover", onCommentMouseOver);
            radioComment.addEventListener("mouseout", onCommentMouseOut);
        }
        if (radioInPost) {
            radioInPost.style.accentColor = "#193456";
            radioInPost.addEventListener("mouseover", onInPostMouseOver);
            radioInPost.addEventListener("mouseout", onInPostMouseOut);
        }

        // Cleanup event listeners on unmount
        return () => {
            if (radioComment) {
                radioComment.removeEventListener(
                    "mouseover",
                    onCommentMouseOver
                );
                radioComment.removeEventListener("mouseout", onCommentMouseOut);
            }
            if (radioInPost) {
                radioInPost.removeEventListener("mouseover", onInPostMouseOver);
                radioInPost.removeEventListener("mouseout", onInPostMouseOut);
            }
        };
    }, [platform.name]);

    return (
        <Card className="border-white border-none shadow-none">
            <CardContent className="border-none border-white p-0">
                <div className="flex flex-col justify-center items-start">
                    <div className="flex flex-col w-full">
                        <div className="flex items-center gap-2 mt-2">
                            <h4 className="text-sm font-semibold text-primary">
                                Include Footer
                            </h4>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() =>
                                                setOpenInfoDialog(true)
                                            }
                                        >
                                            <Info className="h-4 w-4" />
                                            <span className="sr-only">
                                                Footer info
                                            </span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-xs text-white">
                                            Click for examples of footer options
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <Switch
                                checked={platform.includeFooter}
                                onCheckedChange={handleFooterSwitch}
                            />
                        </div>

                        {platform.includeFooter && (
                            <>
                                <span className="font-semibold text-primary mb-1 text-sm">
                                    Listing Information:
                                </span>
                                <Textarea
                                    value={[platform.footer]}
                                    onChange={(e) =>
                                        handleFooterChange(e.target.value)
                                    }
                                    placeholder={
                                        platform.name ===
                                        SocialPlatforms.Instagram
                                            ? "Enter text to be sent as the first comment for your Instagram post. Please refrain from including the MLS number, phone numbers or property address as there is a high probability that Instagram will flag the comment as spam and it will not be shown to your followers."
                                            : `Enter text to be sent as the first comment for your ${platform.name} post.`
                                    }
                                    className="min-h-[270px]"
                                />
                                {/* Character limit indicator (weighted) */}
                                <span
                                    className={`text-xs flex justify-end mt-1 ${
                                        currentWeightedCount > maxChars
                                            ? "text-red-500"
                                            : "text-black"
                                    }`}
                                >
                                    {currentWeightedCount} / {maxChars}{" "}
                                    characters
                                </span>

                                {platform.name === SocialPlatforms.Google && (
                                    <div className="my-2 p-2 border border-red-300 bg-red-50 rounded">
                                        <p className="text-xs text-red-600">
                                            Note: Adding phone numbers will
                                            block the post from being published
                                            or may be removed by Google.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {platform.includeFooter && platform.name !== SocialPlatforms.Google && (
                    <div
                        className={`flex ${
                            isMobile
                                ? "flex-col justify-center mt-2"
                                : "flex-row items-center"
                        } justify-start`}
                    >
                        <div className={`flex items-center gap-2`}>
                            <h4 className="text-sm font-semibold text-primary">
                                Footer Placement
                            </h4>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() =>
                                                setOpenInfoDialog(true)
                                            }
                                        >
                                            <Info className="h-4 w-4" />
                                            <span className="sr-only">
                                                Footer placement info
                                            </span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-xs text-white">
                                            Click for examples of footer
                                            placement options
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>

                        <div
                            className={`${
                                isMobile ? "grid grid-cols-2 w-full" : "flex items-center gap-2 ml-4"
                            }`}
                        >
                            {platform.name === SocialPlatforms.Pinterest ? null : (
                                <label
                                    className={`flex items-center gap-1 text-sm ${
                                        platform.footerType === "comment"
                                            ? "text-primary"
                                            : "text-muted"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        id={`${platform.name}-comment-radio`}
                                        name={`${platform.name}-footerType`}
                                        value="comment"
                                        checked={
                                            platform.footerType === "comment"
                                        }
                                        onChange={() =>
                                            handleFooterTypeChange(
                                                platform.name,
                                                "comment"
                                            )
                                        }
                                        disabled={
                                            (platform.name.toLowerCase() ===
                                                "twitter" &&
                                                !platform.threads) ||
                                            platform.name.toLowerCase() ===
                                                "pinterest"
                                        }
                                    />
                                    As comment
                                </label>
                            )}

                            {platform.name === SocialPlatforms.Instagram ||
                            platform.name === SocialPlatforms.Twitter ? null : (
                                <label
                                    className={`flex items-center gap-1 text-sm ${
                                        platform.footerType === "in-post"
                                            ? "text-primary"
                                            : "text-muted"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        id={`${platform.name}-inpost-radio`}
                                        name={`${platform.name}-footerType`}
                                        value="in-post"
                                        checked={
                                            platform.footerType === "in-post"
                                        }
                                        onChange={() =>
                                            handleFooterTypeChange(
                                                platform.name,
                                                "in-post"
                                            )
                                        }
                                        disabled={
                                            (platform.name.toLowerCase() ===
                                                "twitter" &&
                                                !platform.threads) ||
                                            platform.name.toLowerCase() ===
                                                "instagram"
                                        }
                                    />
                                    In post
                                </label>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>

            <ConfirmPopup
                open={confirmFooterOff}
                title={FOOTER_OFF_WARNING_TITLE}
                message={FOOTER_OFF_WARNING_MESSAGE}
                messageTwo={FOOTER_OFF_WARNING_MESSAGE_TWO}
                confirmText={FOOTER_OFF_CONFIRM_TEXT}
                cancelText={FOOTER_OFF_CANCEL_TEXT}
                onConfirm={confirmFooterOffAndTurnOff}
                onCancel={() => setConfirmFooterOff(false)}
            />

            <div className="relative w-full h-full">
                <PostFooterInfo
                    openInfoDialog={openInfoDialog}
                    setOpenInfoDialog={setOpenInfoDialog}
                    parsedFooter={platform.footer}
                    platformName={platform.name}
                />
            </div>
        </Card>
    );
};

export default PlatformFooterCard;
